// =============================================================================
// me.ts — current-user state hook.
//
// Patch 009. Fetches /api/me on mount. Used by Header (UserChip rendering)
// and CommandBoardPage (Run AI Suggestions visibility). Cached in module
// state once resolved so multiple components share one fetch.
// =============================================================================

import { useEffect, useState } from 'react'

import { api, type MeResponse } from './api'

export type MeState =
  | { kind: 'loading' }
  | { kind: 'ready'; me: MeResponse }
  | { kind: 'error'; message: string }

let cached: MeState = { kind: 'loading' }
let inFlight: Promise<MeResponse> | null = null
const subscribers = new Set<(s: MeState) => void>()

function publish(s: MeState): void {
  cached = s
  for (const fn of subscribers) fn(s)
}

async function loadMe(): Promise<void> {
  if (inFlight) return
  inFlight = api
    .me()
    .then((me) => {
      publish({ kind: 'ready', me })
      return me
    })
    .catch((err: Error) => {
      publish({ kind: 'error', message: err.message })
      throw err
    })
    .finally(() => {
      inFlight = null
    })
}

export function useMe(): MeState {
  const [state, setState] = useState<MeState>(cached)
  useEffect(() => {
    subscribers.add(setState)
    if (cached.kind === 'loading') void loadMe()
    return () => {
      subscribers.delete(setState)
    }
  }, [])
  return state
}
